import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { SeverityBadge, EmptyState, ErrorState, LoadingState, SectionCard } from '@/components/primitives';
import { SEVERITY } from '@/lib/severity';

export const TACTICS = [
  'Reconnaissance', 'Resource Development', 'Initial Access', 'Execution',
  'Persistence', 'Privilege Escalation', 'Defense Evasion', 'Credential Access',
  'Discovery', 'Lateral Movement', 'Collection', 'Command and Control',
  'Exfiltration', 'Impact',
];

const KEYWORDS = [
  ['recon', 'Reconnaissance'], ['resource', 'Resource Development'], ['initial', 'Initial Access'],
  ['execut', 'Execution'], ['persist', 'Persistence'], ['privilege', 'Privilege Escalation'],
  ['escal', 'Privilege Escalation'], ['evas', 'Defense Evasion'], ['bypass', 'Defense Evasion'],
  ['credential', 'Credential Access'], ['discover', 'Discovery'], ['lateral', 'Lateral Movement'],
  ['collect', 'Collection'], ['command', 'Command and Control'], ['c2', 'Command and Control'],
  ['exfiltrat', 'Exfiltration'], ['impact', 'Impact'], ['destruct', 'Impact'],
];

/** Free-text tactic tag to its ATT&CK tactic name (mirrors the backend's normalize_tactic). */
export function normalizeTactic(raw) {
  const lower = String(raw || '').toLowerCase().trim();
  const base = lower.split('(')[0].trim();
  const direct = TACTICS.find((t) => t.toLowerCase() === base || (base && (base.includes(t.toLowerCase()) || t.toLowerCase().includes(base))));
  if (direct) return direct;
  const hit = KEYWORDS.find(([k]) => lower.includes(k));
  return hit ? hit[1] : String(raw || '').split('(')[0].trim();
}

const SEV_ORDER = ['Critical', 'High', 'Medium', 'Low'];

function heatLevel(count, max) {
  if (!count) return 0;
  const share = count / Math.max(max, 1);
  if (share > 0.75) return 4;
  if (share > 0.5) return 3;
  if (share > 0.25) return 2;
  return 1;
}

function SeverityBar({ severity, total }) {
  const parts = SEV_ORDER.filter((s) => severity?.[s]);
  return (
    <span className="dash-sevbar" aria-hidden="true">
      {parts.map((s) => (
        <span key={s} style={{ width: `${(severity[s] / total) * 100}%`, background: SEVERITY[s].color }} />
      ))}
    </span>
  );
}

const sevText = (severity) => SEV_ORDER.filter((s) => severity?.[s]).map((s) => `${severity[s]} ${s.toLowerCase()}`).join(', ');

/**
 * ATT&CK tactic coverage for the briefing window (GET /dashboard/mitre-coverage).
 *
 * Each tile counts distinct stories tagged with that tactic. The stories'
 * TTPs are free-text phrases from the analysis model, not ATT&CK technique
 * IDs, so they are shown only as "recurring phrases" and never as techniques.
 */
export default function MitreHeatmap({ resource, windowLabel }) {
  const { data, error, loading, reload } = resource;
  const [selected, setSelected] = useState(null);

  const max = useMemo(() => Math.max(1, ...(data?.tactics || []).map((t) => t.story_count)), [data]);
  const current = data?.tactics?.find((t) => t.tactic === selected && t.story_count > 0) || null;

  if (!data) {
    if (error) return <ErrorState title="Couldn't load ATT&CK coverage" error={error} onRetry={reload} />;
    return <LoadingState label="ATT&CK coverage" rows={4} />;
  }
  if (!data.total_stories || !data.active_tactics) {
    return (
      <EmptyState
        icon={Shield}
        title="No tactic tags in this window"
        description={data.total_stories
          ? `${data.total_stories} threats matched in the ${windowLabel.toLowerCase()}, but none carry an ATT&CK tactic tag yet.`
          : `No threats matched in the ${windowLabel.toLowerCase()}. Try a longer window.`}
      />
    );
  }

  return (
    <div data-testid="mitre-coverage">
      {error && <ErrorState variant="inline" title="Couldn't refresh ATT&CK coverage" description="Showing the last data that loaded." error={error} onRetry={reload} className="mb-3" />}
      <p className="dash-panel-intro">
        <strong style={{ color: 'var(--text-primary)' }}>{data.mapped_stories} of {data.total_stories} threats</strong> in the {windowLabel.toLowerCase()} are
        tagged with at least one of {data.active_tactics} observed tactics (of 14). Each tile counts distinct threats; a threat tagged with
        several tactics counts once in each. TTPs come from the analysis as free text and are not mapped to ATT&CK technique IDs.
      </p>

      <div className="dash-mitre-grid" role="list" aria-label="ATT&CK tactics">
        {data.tactics.map((t) => {
          const empty = !t.story_count;
          const pressed = selected === t.tactic;
          const label = `${t.tactic} (${t.tactic_id}): ${t.story_count} ${t.story_count === 1 ? 'threat' : 'threats'}${empty ? '' : `, ${sevText(t.severity)}`}`;
          const body = (
            <>
              <span className="t-meta">{t.tactic_id}</span>
              <span className="dash-tactic-name">{t.tactic}</span>
              <span className="dash-tactic-count">
                {t.story_count}<small>{t.story_count === 1 ? 'threat' : 'threats'}</small>
              </span>
              {!empty && <SeverityBar severity={t.severity} total={t.story_count} />}
              {t.top_ttps?.length > 0 && (
                <span className="dash-tactic-phrases">{t.top_ttps.slice(0, 2).map((p) => p.text).join(' · ')}</span>
              )}
            </>
          );
          return (
            <div role="listitem" key={t.tactic} style={{ display: 'flex' }}>
              {empty ? (
                <div className="dash-tactic dash-tactic--empty" style={{ flex: 1 }}>{body}</div>
              ) : (
                <button
                  type="button"
                  className="dash-tactic"
                  style={{ flex: 1 }}
                  data-heat={heatLevel(t.story_count, max)}
                  aria-pressed={pressed}
                  aria-controls="mitre-tactic-detail"
                  aria-label={`${label}. Show threats`}
                  onClick={() => setSelected(pressed ? null : t.tactic)}
                >
                  {body}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="dash-legend" aria-hidden="true">
        <span>Severity of tagged threats:</span>
        {SEV_ORDER.map((s) => (
          <span key={s}><span className="dash-legend-swatch" style={{ background: SEVERITY[s].color }} />{s}</span>
        ))}
        <span>Tile tint = share of threats</span>
      </div>

      <div id="mitre-tactic-detail" aria-live="polite">
        {current && (
          <SectionCard
            className="dash-mitre-detail"
            as="h3"
            title={`${current.tactic} · ${current.tactic_id}`}
            subtitle={`${current.story_count} ${current.story_count === 1 ? 'threat' : 'threats'} in the ${windowLabel.toLowerCase()} · ${sevText(current.severity)}`}
          >
            {current.top_ttps?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div className="t-label" style={{ marginBottom: 8 }}>Recurring TTP phrases in these threats</div>
                <div className="dash-chips">
                  {current.top_ttps.map((p) => (
                    <span key={p.text} className="dash-chip">{p.text}<b>{p.count}</b></span>
                  ))}
                </div>
                <p className="dash-caption" style={{ marginTop: 8 }}>
                  Phrases shared by two or more threats, with the number of threats using each. {current.distinct_ttp_phrases} distinct phrases in total.
                </p>
              </div>
            )}
            <div className="t-label" style={{ marginBottom: 4 }}>Threats</div>
            <ul className="dash-sublist">
              {current.stories.map((s) => (
                <li key={s.attack_id}>
                  <Link to={`/threats?id=${encodeURIComponent(s.attack_id)}`}>
                    <SeverityBadge severity={s.severity} short size="sm" />
                    <span className="title">{s.name}</span>
                    {s.threat_actor && <span className="t-meta is-hide-sm">{s.threat_actor}</span>}
                  </Link>
                </li>
              ))}
            </ul>
            {current.story_count > current.stories.length && (
              <p className="dash-caption">Showing the {current.stories.length} most severe of {current.story_count}.</p>
            )}
          </SectionCard>
        )}
      </div>
    </div>
  );
}
